import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

@Injectable({
    providedIn: 'root',
})
export class SmoothScrollService {
    private lenis: Lenis | undefined;
    private ngZone = inject(NgZone);
    private platformId = inject(PLATFORM_ID);
    private router = inject(Router);

    constructor() {
        gsap.registerPlugin(ScrollTrigger);
    }

    init(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            this.lenis = new Lenis({
                duration: 1.2,
                easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
                orientation: 'vertical',
                gestureOrientation: 'vertical',
                smoothWheel: true,
                wheelMultiplier: 1,
                touchMultiplier: 2,
                infinite: false,
            });

            // Synchronize Lenis with GSAP ScrollTrigger
            this.lenis.on('scroll', ScrollTrigger.update);

            gsap.ticker.add((time) => {
                this.lenis?.raf(time * 1000);
            });

            gsap.ticker.lagSmoothing(0);

            console.log('Lenis initialized');
        });

        // New Scroll to top on navigation
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => {
                const trigger = this.router.lastSuccessfulNavigation?.trigger;
                if (trigger === 'imperative') {
                    this.lenis?.scrollTo(0, { immediate: true });
                }
            });
    }

    get lenisInstance(): Lenis | undefined {
        return this.lenis;
    }

    scrollTo(target: any, options?: any): void {
        this.lenis?.scrollTo(target, options);
    }

    stop(): void {
        this.lenis?.stop();
    }

    start(): void {
        this.lenis?.start();
    }

    destroy(): void {
        this.lenis?.destroy();
    }
}
